import type { ReactNode } from "react";
import { FilterX, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface UnifiedFilterRowProps {
  slug: string;
  search: string;
  onSearchChange: (v: string) => void;
  /** Global reset — returns to the pristine "All" view. The button
   *  is dumb: it just calls this, it doesn't know the filter shape. */
  onClearFilters: () => void;
  /** True when the current view differs from the "All" default. The
   *  button always renders (reserved slot, no layout shift) but is
   *  visually hidden + non-interactive when this is false. */
  showClearFilters: boolean;
  /** Toolbar-variant "+ Add filter" button rendered when there are
   *  zero active chips (the chips bar takes over once at least one
   *  chip exists). Null hides the slot entirely. */
  addFilterSlot: ReactNode;
  /** Toolbar-variant "Sort" button — F4. Rendered when zero sorts
   *  are active (the chip-bar "Sort" inside FilterChipsBar takes
   *  over once any sort exists). Null hides the slot entirely. */
  addSortSlot: ReactNode;
  /** Rendered next to the Add filter slot. The page owns the column
   *  preferences state + selector, so it passes the pre-wired
   *  component in via a slot rather than re-plumbing the column hook
   *  into this row. */
  columnsSlot?: ReactNode;
}

/** The Companies-page filter toolbar. After F5 it's a single row
 *  with: search input → Clear filters → +Add filter (zero-state)
 *  → Sort (zero-state) → Columns. The dual-handle score slider
 *  and the QuickStatusFilters pill row that used to sit alongside
 *  were retired — both surfaced filters that the chips bar +
 *  "+ Add filter" picker already exposes via the regular
 *  advanced-filter path. */
export function UnifiedFilterRow({
  search,
  onSearchChange,
  onClearFilters,
  showClearFilters,
  addFilterSlot,
  addSortSlot,
  columnsSlot,
}: UnifiedFilterRowProps) {
  return (
    // gap-2 on the row — moderate spacing between the search input
    // and the inline "+ Add filter" / "Sort" toolbar slots so the
    // buttons read as siblings of the search bar, not as a far-right
    // cluster. The right-aligned group (Clear filters + any future
    // residual columnsSlot) is split out below and pushed right with
    // ml-auto.
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative max-w-sm flex-1">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search a company, a director or a phone number…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") onSearchChange("");
          }}
          // pr-9 is constant so the X appearing causes no layout
          // shift — the button is absolutely positioned over it.
          className="h-9 pl-9 pr-9"
        />
        {search !== "" && (
          <button
            type="button"
            aria-label="Clear search"
            onClick={() => onSearchChange("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Discovery slots: "+ Add filter" + "Sort" toolbar variants
       *  sit immediately after the search bar so they're visually
       *  tied to the search/filter intent rather than drifting out
       *  to the right edge. */}
      {addFilterSlot}
      {addSortSlot}

      {/* Right-aligned cluster. ml-auto on the wrapper pushes it to
       *  the far edge; the inner gap-2 keeps the buttons together.
       *  Clear filters is always mounted (invisible when there's
       *  nothing to clear) to reserve its slot and avoid layout
       *  shift on the first filter. */}
      <div className="ml-auto flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onClearFilters}
          aria-label="Clear filters"
          className={cn(
            "text-slate-700",
            !showClearFilters && "invisible pointer-events-none",
          )}
        >
          <FilterX className="mr-2 h-3.5 w-3.5" />
          Clear filters
        </Button>
        {columnsSlot}
      </div>
    </div>
  );
}
